'use client';

import React, { useState } from 'react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { useOrganizationLocaleSettings } from '@/lib/platform/hooks/use-organization-locale';
import type { NumberFormatType } from '@/lib/platform/utils/locale-formatting';

// Map number format type to locale for Intl.NumberFormat
function getLocaleFromNumberFormat(numberFormat: NumberFormatType): string {
  switch (numberFormat) {
    case 'us':
      return 'en-US'; // US: 1,234,567.89
    case 'eu':
      return 'da-DK'; // EU/Denmark: 1.234.567,89
    case 'fr':
      return 'fr-FR'; // France: 1 234 567,89
    default:
      return 'da-DK';
  }
}

interface NumberInputProps {
  label?: React.ReactNode; // Support string or JSX (e.g., with Tooltip)
  value: number | '';
  onChange: (value: number | '') => void;
  suffix?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  id?: string;
  numberFormat?: NumberFormatType; // Optional override - defaults to organization setting
  decimals?: number; // Number of decimal places (0 = integer only, 2 = 0.00, etc.)
}

export default function NumberInput({
  label,
  value,
  onChange,
  suffix,
  placeholder = '0',
  required,
  disabled,
  error,
  helperText,
  id,
  numberFormat, // If not provided, will use organization setting
  decimals = 0, // Default to integers only
}: NumberInputProps) {
  const { darkMode } = useDarkMode();
  const { numberFormat: orgNumberFormat } = useOrganizationLocaleSettings();

  // Use provided numberFormat or fall back to organization setting
  const activeNumberFormat = numberFormat || orgNumberFormat;

  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  // Convert number format to locale for Intl.NumberFormat
  const locale = React.useMemo(
    () => getLocaleFromNumberFormat(activeNumberFormat),
    [activeNumberFormat]
  );

  // Generate a unique ID if not provided (using React.useId for SSR compatibility)
  const generatedId = React.useId();
  const inputId = id || generatedId;

  // Get decimal separator for current number format
  const getDecimalSeparator = React.useCallback(() => {
    const numberWithDecimal = 1.1;
    const formatted = new Intl.NumberFormat(locale).format(numberWithDecimal);
    return formatted.charAt(1); // Gets the separator (. or ,)
  }, [locale]);

  // Format number for display based on number format
  // EU: 1.234.567,89 | US: 1,234,567.89 | FR: 1 234 567,89
  const formatNumber = React.useCallback(
    (num: number | '') => {
      if (num === '' || num === 0) return '';
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(num);
    },
    [locale, decimals]
  );

  // Clean input string (remove invalid characters but allow incomplete decimal input)
  const cleanInput = React.useCallback(
    (str: string): string => {
      if (decimals === 0) {
        // Integer only: Remove all non-digit characters
        return str.replace(/[^\d]/g, '');
      } else {
        // Decimals allowed: Keep digits and decimal separator
        const decimalSeparator = getDecimalSeparator();
        const regex = new RegExp(`[^\\d${decimalSeparator.replace('.', '\\.')}]`, 'g');
        return str.replace(regex, '');
      }
    },
    [decimals, getDecimalSeparator]
  );

  // Parse display string to number
  const parseNumber = (str: string): number | '' => {
    if (decimals === 0) {
      // Integer only: Remove all non-digit characters
      const cleanValue = str.replace(/[^\d]/g, '');
      if (cleanValue === '') return '';
      return Number(cleanValue);
    } else {
      // Decimals allowed: Keep digits and decimal separator
      const decimalSeparator = getDecimalSeparator();
      const regex = new RegExp(`[^\\d${decimalSeparator.replace('.', '\\.')}]`, 'g');
      let cleanValue = str.replace(regex, '');

      // Replace locale-specific decimal separator with standard dot for parsing
      if (decimalSeparator !== '.') {
        cleanValue = cleanValue.replace(decimalSeparator, '.');
      }

      if (cleanValue === '') return '';
      const numValue = parseFloat(cleanValue);
      return isNaN(numValue) ? '' : numValue;
    }
  };

  // Update display when value changes externally or on blur (adjust-state-during-render
  // pattern, https://react.dev/learn/you-might-not-need-an-effect)
  const formattedValue = formatNumber(value);
  const [prevSync, setPrevSync] = useState<{ formatted: string; isFocused: boolean } | null>(null);
  if (
    prevSync === null ||
    prevSync.formatted !== formattedValue ||
    prevSync.isFocused !== isFocused
  ) {
    setPrevSync({ formatted: formattedValue, isFocused });
    if (!isFocused) {
      setDisplayValue(formattedValue);
    }
  }

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Clean input (remove invalid characters but allow incomplete decimals like "5,")
    const cleanedInput = cleanInput(inputValue);

    // Parse to number
    const numValue = parseNumber(cleanedInput);

    // While typing (focused), show cleaned input WITHOUT formatting
    // This allows user to type decimals (e.g., "5," then "5,2")
    // Formatting happens on blur via the render-sync block above
    setDisplayValue(cleanedInput);
    onChange(numValue);
  };

  return (
    <div>
      {label && (
        <label
          htmlFor={inputId}
          className={`mb-2 block text-[13px] font-medium ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          {label} {suffix && `(${suffix})`}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}
      <input
        id={inputId}
        type="text"
        inputMode="numeric"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={`block w-full [appearance:textfield] rounded-md px-3 py-1.5 text-base outline-1 -outline-offset-1 transition-all placeholder:text-gray-400 sm:text-sm/6 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
          darkMode
            ? 'bg-white/5 text-gray-100 outline-white/10'
            : 'bg-white text-gray-900 outline-gray-300'
        } ${
          darkMode
            ? 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500'
            : 'focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600'
        } ${
          error
            ? darkMode
              ? 'outline-red-500/50 focus:outline-red-500'
              : 'outline-red-500 focus:outline-red-500'
            : ''
        } ${disabled ? 'disabled:cursor-not-allowed disabled:opacity-60' : ''} `}
      />
      {(error || helperText) && (
        <p
          className={`mt-2 text-sm ${
            error ? 'text-red-500' : darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          {error || helperText}
        </p>
      )}
    </div>
  );
}
